// Master Apps · edição de aplicação (`/admin/applications/:slug/edit`).
//
// Carrega a aplicação pelo slug e entrega a MESMA tela do cadastro em modo
// edição (prefill completo + PATCH). Loading, falha e 404 seguem o padrão das
// outras páginas de dados (ScreenLoading + ScreenError), nunca spinner infinito.

import type { ApplicationCreateInput } from '@masterapps/shared';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ScreenLoading } from '@/components/loading';
import { ScreenError, screenErrorKind } from '@/components/screen-error';
import { type RegisterSubmitHandlers, ScreenRegister } from '@/components/screens/register-screen';
import { useApplication, useUpdateApplication } from '@/hooks/use-applications';
import { useScreenProps } from '@/store/theme';

export function EditApplicationPage() {
  const sp = useScreenProps();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { data: app, isLoading, isError, error, refetch, isFetching } = useApplication(slug);
  const update = useUpdateApplication();

  if (isLoading) return <ScreenLoading dark={sp.dark} />;

  if (isError || !app) {
    const failure = error ?? new Error('Aplicação não encontrada');
    const missing = screenErrorKind(failure) === 'notFound';
    return (
      <ScreenError
        error={failure}
        onRetry={slug ? () => void refetch() : undefined}
        retrying={isFetching}
        active="inventory"
        title="Editar aplicação"
        breadcrumb={['Administração', 'Inventário']}
        role="admin"
        heading={missing ? 'Aplicação não encontrada' : undefined}
        description={
          missing
            ? 'Esta aplicação pode ter sido removida do inventário ou o link que você abriu está desatualizado.'
            : undefined
        }
      />
    );
  }

  // O PATCH é por id (o slug fica bloqueado na edição justamente por ser a URL
  // pública). `handlers.onSuccess` descarta o rascunho da edição antes de sair.
  const onSubmit = (input: ApplicationCreateInput, handlers: RegisterSubmitHandlers) => {
    update.mutate(
      { id: app.id, input },
      {
        onSuccess: () => {
          handlers.onSuccess();
          toast.success('Alterações salvas');
          navigate('/admin/inventory');
        },
        onError: (e: Error) => {
          const painted = handlers.onError(e);
          toast.error(painted ? 'Revise os campos destacados no formulário' : e.message);
        },
      },
    );
  };

  return (
    <div style={{ height: 'var(--app-vh)', overflow: 'hidden' }}>
      <ScreenRegister
        {...sp}
        mode="edit"
        initial={app}
        onSubmit={onSubmit}
        submitting={update.isPending}
      />
    </div>
  );
}
